import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { UserData } from './models/user-data.model';
import { SecureStorageService } from '../secure-storage/secure-storage-service';
import { Events } from '../events/events';
import { PractitionerModel } from 'src/app/api/proxy/practice/practice-models';

const CLINIC_GROUP_CACHE_KEY_SUFFIX = '_selectedClinicGroup';

export class UserInfoService {
  static normalizeEmailKey(email: string): string {
    return email.trim().toLowerCase();
  }

  static async setUserInfo(val: UserData | null, emitChanges: boolean = true) {
    if (val != null) {
      if (val?.tokenClaims.oid) {
        val.sub = val?.tokenClaims.oid;
        val.tokenClaims.sub = val?.tokenClaims.oid;
      }
    }
    if (val) {
      await SecureStorageService.save(SecureStorageKey.CurrentUser, val);
    } else {
      await SecureStorageService.remove(SecureStorageKey.CurrentUser);
    }

    if (emitChanges) Events.userUpdated.emit(val);
  }

  static async getUserInfo(): Promise<UserData | null> {
    return await SecureStorageService.getById<UserData>(SecureStorageKey.CurrentUser);
  }

  static async setClinicGroupForEmail(email: string, clinicGroupId: string): Promise<void> {
    const normalizedEmail = this.normalizeEmailKey(email);
    await SecureStorageService.saveWithPrefix(
      `${normalizedEmail}${CLINIC_GROUP_CACHE_KEY_SUFFIX}`,
      clinicGroupId,
      SecureStorageService.Prefix.ClinicGroupSelection,
    );
  }

  static async getClinicGroupForEmail(email: string): Promise<string | null> {
    const normalizedEmail = this.normalizeEmailKey(email);
    return await SecureStorageService.getWithPrefix<string>(
      `${normalizedEmail}${CLINIC_GROUP_CACHE_KEY_SUFFIX}`,
      SecureStorageService.Prefix.ClinicGroupSelection,
    );
  }

  static async clearClinicGroupForEmail(email: string): Promise<void> {
    const normalizedEmail = this.normalizeEmailKey(email);
    await SecureStorageService.removeWithPrefix(
      `${normalizedEmail}${CLINIC_GROUP_CACHE_KEY_SUFFIX}`,
      SecureStorageService.Prefix.ClinicGroupSelection,
    );
  }

  /** Sync practitioner profile fields to CurrentUser and the per-email User cache. */
  static async updateFromPractitionerModel(
    practitioner: PractitionerModel,
  ): Promise<UserData | null> {
    const userData = await UserInfoService.getUserInfo();
    if (!userData) {
      return null;
    }

    userData.given_name = practitioner.firstName ?? userData.given_name;
    userData.family_name = practitioner.lastName ?? userData.family_name;
    userData.middleInitial = practitioner.middleInitial ?? userData.middleInitial;
    userData.suffix = practitioner.suffix ?? userData.suffix;
    userData.credentials = practitioner.credentials ?? userData.credentials;
    userData.profile_picture = practitioner.profilePictureBase64 ?? null;

    await UserInfoService.setUserInfo(userData);

    if (userData.email) {
      await SecureStorageService.saveWithPrefix<IPersistedUserInfo>(
        userData.email,
        {
          userName: userData.email,
          firstName: practitioner.firstName ?? '',
          lastName: practitioner.lastName ?? '',
          photo: practitioner.profilePictureBase64 ?? null,
        },
        SecureStorageService.Prefix.User,
      );
    }

    return userData;
  }
}

export interface IPersistedUserInfo {
  userName: string;
  firstName: string;
  lastName: string;
  photo: any;
}
